import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InviteLinkBox } from "@/components/PendingInvites";
import { AddAdminDialog } from "@/components/admin/AddAdminDialog";
import { AdminAccountRow } from "@/components/admin/AdminAccountRow";
import { ShieldCheck, Plus, Mail, Clock, Send, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";

function PendingAdminInvite({ inv, onChange }) {
  const [busy, setBusy] = useState("");
  const [fresh, setFresh] = useState(null);
  const resend = async () => {
    setBusy("resend");
    try { const { data } = await api.post(`/invites/${inv.id}/resend`); setFresh(data); toast.success(data.email_result?.sent ? `Invitation re-sent to ${inv.email}` : "Fresh link created"); onChange(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const revoke = async () => {
    setBusy("revoke");
    try { await api.delete(`/invites/${inv.id}`); toast.success("Invitation revoked"); onChange(); } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };
  const daysLeft = Math.max(0, Math.ceil((new Date(inv.expires_at) - Date.now()) / 86400000));
  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-emerald-50/30 text-slate-900 p-3.5" data-testid={`admin-invite-${inv.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900">{inv.name} <span className="text-xs font-normal text-slate-500">· invited</span></div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {inv.email}</span>
            <span className={`flex items-center gap-1 ${inv.expired ? "text-rose-600 font-semibold" : ""}`}><Clock className="h-3 w-3" /> {inv.expired ? "Link expired" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left on link`}</span>
            <span>{inv.setup?.trial_days}-day trial · ${inv.setup?.plan_amount}/mo</span>
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={resend} disabled={!!busy} data-testid={`admin-invite-resend-${inv.id}`}>{busy === "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {inv.expired ? "Renew & send" : "Resend"}</Button>
        <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-rose-600" onClick={revoke} disabled={!!busy} data-testid={`admin-invite-revoke-${inv.id}`}><Ban className="h-3.5 w-3.5" /></Button>
      </div>
      {fresh && <div className="mt-3"><InviteLinkBox url={fresh.invite_url} emailResult={fresh.email_result} email={inv.email} /></div>}
    </div>
  );
}

export function AdminAccountsCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/accounts").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="rounded-2xl border border-slate-900/10 bg-slate-900 text-white p-6 mb-5" data-testid="admin-accounts-card">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></span>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display font-bold text-lg">Admin accounts <span className="ml-1 rounded-full bg-white/15 text-[10px] font-bold px-2 py-0.5 align-middle">SUPER ADMIN</span></h2>
          <p className="text-sm text-white/60">Create separate landlord accounts, set their free-trial length and monthly fee, and manage their access. Only you can see this.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2 bg-white text-slate-900 hover:bg-slate-100" data-testid="add-admin-btn"><Plus className="h-4 w-4" /> Add admin</Button>
      </div>
      <div className="mt-5 space-y-2.5">
        {!data ? <p className="text-sm text-white/50">Loading…</p> : (
          <>
            {data.invites.map((inv) => <PendingAdminInvite key={inv.id} inv={inv} onChange={load} />)}
            {data.accounts.map((a) => <AdminAccountRow key={a.id} acct={a} onChange={load} />)}
            {data.accounts.length === 0 && data.invites.length === 0 && (
              <p className="text-sm text-white/60 text-center py-6 rounded-xl border border-dashed border-white/20" data-testid="admin-accounts-empty">No other admin accounts yet — add one to give another landlord their own VuZuRent.</p>
            )}
          </>
        )}
      </div>
      <AddAdminDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
